import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Images } from "lucide-react";
import { useRecordCollections } from "@/api/hooks";
import { formatDate, pluralize } from "@/common/utils";
import { HGAnyRecord, HGCollection } from "@/common/types";

/**
 * How much of `content` is worth parsing for the preview line.
 *
 * The line is clamped to two lines by CSS, so everything past roughly this is
 * thrown away after being parsed — and `content` runs to 50 000 characters.
 * Running a markdown parser over all of it, for every row, on every render, was
 * the single most expensive thing in the list.
 */
const PREVIEW_CHARS = 300;

function previewOf(content: string) {
  return content.length > PREVIEW_CHARS
    ? content.slice(0, PREVIEW_CHARS) + "…"
    : content;
}

interface RecordBodyProps {
  record: HGAnyRecord;
  /**
   * The collection being browsed, if any. Passed in rather than read from the
   * route so the row renders correctly outside a `:collectionID` page too.
   */
  currentCollectionID?: string;
  /**
   * `id → collection`, when the caller already has one. A list hands this down
   * so its rows share one index instead of each opening a query subscription
   * and filtering the whole collection list for itself.
   */
  collectionsByID?: Map<string, HGCollection>;
  /** Title rendered in normal weight and secondary ink (ongoing, repeated). */
  dimmed?: boolean;
  /** Extra content placed on the meta line, before the collection badge. */
  meta?: React.ReactNode;
  /** Extra content placed at the very end of the row, after the badge. */
  trailing?: React.ReactNode;
}

/**
 * The card a record sits in, in both views. Kept here beside the body so the
 * shell and its contents stay one design. Shared with RecordsNotes and
 * RecordsTimeline, at the cost of this file not being a clean Fast Refresh
 * boundary.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function recordCardClass(selected: boolean) {
  return `cursor-pointer rounded-lg border px-[14px] py-[11px] transition-colors ${
    selected
      ? "border-primary bg-accent"
      : "border-border bg-card hover:bg-row-hover"
  }`;
}

/**
 * Everything a record row shows apart from its position marker: title, preview,
 * attachments, thumbnail, collection. Shared by the timeline row and the note
 * card so the two never drift apart.
 */
export default function RecordBody({
  record,
  currentCollectionID,
  collectionsByID,
  dimmed,
  meta,
  trailing,
}: RecordBodyProps) {
  const collections = useRecordCollections(
    record.collection_id,
    collectionsByID,
  );

  const hasTitle = !!record.title;
  const hasContent = !!record.content;

  // Hide the badge entirely when the record only belongs to the collection
  // the user is already browsing — showing it there is redundant.
  const showCollectionBadge = !(
    collections.length === 1 && collections[0].id === currentCollectionID
  );
  const extraCollectionsCount = collections.length - 1;

  // Summary of attachments/links (e.g. "2 photos, 1 link"), or null if none.
  const mediaCounts: [number | undefined, string][] = [
    [record.media_count, "photo"],
    [record.media_audio_count, "audio"],
    [record.media_document_count, "document"],
    [record.url.length, "link"],
  ];
  const mediaParts = mediaCounts
    .filter(([count]) => !!count)
    .map(([count, noun]) => pluralize(count as number, noun));
  const mediaSummary = mediaParts.length > 0 ? mediaParts.join(", ") : null;

  // When there's neither title nor content, the title's place shows the
  // creation date instead.
  const createdText = `Record created ${formatDate(record.created)}`;

  // The second line is the content preview when both title and content are
  // present; otherwise it's the media summary (attachments/links), covering
  // both the "title or content, not both" and "neither" cases.
  const showMediaLine = !(hasTitle && hasContent) && !!mediaSummary;

  return (
    <div className="flex items-start gap-3">
      <div className="min-w-0 flex-1">
        <div
          className={`min-w-0 font-serif text-[14.5px] leading-[1.28] line-clamp-1 ${
            dimmed ? "font-normal text-ink2" : "font-semibold text-foreground"
          }`}
        >
          {record.title ||
            (record.content ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {previewOf(record.content)}
              </ReactMarkdown>
            ) : (
              createdText
            ))}
        </div>
        {hasTitle && hasContent && (
          <div className="mt-[5px] line-clamp-2 font-sans text-[13px] leading-[1.5] text-ink2">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {previewOf(record.content)}
            </ReactMarkdown>
          </div>
        )}
        {(showMediaLine || meta) && (
          <div className="mt-[5px] flex items-center gap-2 font-sans text-[11px] text-muted-foreground">
            {meta}
            {showMediaLine && <span>{mediaSummary}</span>}
          </div>
        )}
      </div>

      {record.media_thumbnail_url && (
        <div className="relative h-11 w-11 shrink-0 self-center">
          <img
            src={record.media_thumbnail_url}
            alt=""
            className="h-11 w-11 rounded-md object-cover"
            // Presigned URLs expire in long-lived lists — hide on failure
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
          {!!record.media_count && record.media_count > 1 && (
            <div className="absolute bottom-0.5 right-0.5 flex items-center gap-0.5 rounded bg-black/70 px-1 py-[1px] text-[9px] font-medium leading-none text-white">
              <Images className="size-2.5" />
              {record.media_count}
            </div>
          )}
        </div>
      )}

      {showCollectionBadge && collections.length > 0 && (
        <div
          className="flex max-w-[120px] shrink-0 items-center self-center whitespace-nowrap rounded border border-border bg-card px-2 py-0.5 font-sans text-[10.5px] text-muted-foreground"
          title={collections[0].title}
        >
          <span className="truncate">{collections[0].title}</span>
          {extraCollectionsCount > 0 && (
            <span className="shrink-0">{` +${extraCollectionsCount}`}</span>
          )}
        </div>
      )}

      {trailing}
    </div>
  );
}
